import React, { useState, useEffect } from 'react';
import { adminTaxDocumentsAPI, API_HOST } from '../services/api';
import './PendingRegistrations.css';

interface TaxDocument {
  id: number;
  customer: number;
  customer_name: string;
  document_type: string;
  document_type_display: string;
  file: string;
  original_filename: string;
  expiration_date: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  status_display: string;
  reviewed_by: number | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  review_notes: string;
  created_at: string;
  updated_at: string;
  is_expired: boolean;
  expires_within_30_days: boolean;
  days_until_expiration: number | null;
}

interface Summary {
  pending_count: number;
  expiring_soon_count: number;
  expired_count: number;
}

const TaxDocumentsReview: React.FC = () => {
  const [documents, setDocuments] = useState<TaxDocument[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [selectedDocument, setSelectedDocument] = useState<TaxDocument | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [approveNotes, setApproveNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadDocuments();
    loadSummary();
  }, [statusFilter]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await adminTaxDocumentsAPI.getAll({ status: statusFilter || undefined });
      const data = response.data;
      let docsList: TaxDocument[] = [];
      if (Array.isArray(data)) {
        docsList = data;
      } else if (data?.results && Array.isArray(data.results)) {
        docsList = data.results;
      }
      setDocuments(docsList);
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load tax documents');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const response = await adminTaxDocumentsAPI.getSummary();
      setSummary(response.data);
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateOnly = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handleApprove = async (doc: TaxDocument) => {
    if (!window.confirm(`Are you sure you want to approve this ${doc.document_type_display} from "${doc.customer_name}"?`)) {
      return;
    }

    try {
      setProcessing(true);
      await adminTaxDocumentsAPI.approve(doc.id, approveNotes);
      setSelectedDocument(null);
      setApproveNotes('');
      loadDocuments();
      loadSummary();
    } catch (err: any) {
      console.error('Error approving document:', err);
      alert(err.response?.data?.error || 'Failed to approve document');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedDocument) return;

    if (!rejectNotes.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessing(true);
      await adminTaxDocumentsAPI.reject(selectedDocument.id, rejectNotes);
      setShowRejectModal(false);
      setRejectNotes('');
      setSelectedDocument(null);
      loadDocuments();
      loadSummary();
    } catch (err: any) {
      console.error('Error rejecting document:', err);
      alert(err.response?.data?.error || 'Failed to reject document');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = async (doc: TaxDocument) => {
    try {
      const response = await adminTaxDocumentsAPI.download(doc.id);
      const url = response.data.url;
      if (url.startsWith('http')) {
        window.open(url, '_blank');
      } else {
        window.open(`${API_HOST}${url}`, '_blank');
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download document');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'status-badge status-approved';
      case 'PENDING':
        return 'status-badge status-pending';
      case 'REJECTED':
        return 'status-badge status-rejected';
      case 'EXPIRED':
        return 'status-badge status-expired';
      default:
        return 'status-badge';
    }
  };

  if (loading && documents.length === 0) {
    return (
      <div className="pending-registrations-page">
        <div className="page-header">
          <h1>Tax Document Review</h1>
        </div>
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  return (
    <div className="pending-registrations-page">
      <div className="page-header">
        <h1>Tax Document Review</h1>
        <p className="page-subtitle">Review and approve customer tax exemption documents</p>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards" style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          <div
            className={`summary-card ${statusFilter === 'PENDING' ? 'active' : ''}`}
            onClick={() => setStatusFilter('PENDING')}
            style={{ cursor: 'pointer', flex: 1, padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '8px', border: statusFilter === 'PENDING' ? '2px solid #ffc107' : '1px solid #ddd' }}
          >
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#856404' }}>{summary.pending_count}</div>
            <div style={{ color: '#856404' }}>Pending Review</div>
          </div>
          <div
            className={`summary-card ${statusFilter === 'expiring' ? 'active' : ''}`}
            onClick={() => setStatusFilter('expiring')}
            style={{ cursor: 'pointer', flex: 1, padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '8px', border: statusFilter === 'expiring' ? '2px solid #ffc107' : '1px solid #ddd' }}
          >
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#856404' }}>{summary.expiring_soon_count}</div>
            <div style={{ color: '#856404' }}>Expiring Soon</div>
          </div>
          <div
            className={`summary-card ${statusFilter === 'expired' ? 'active' : ''}`}
            onClick={() => setStatusFilter('expired')}
            style={{ cursor: 'pointer', flex: 1, padding: '1rem', backgroundColor: '#f8d7da', borderRadius: '8px', border: statusFilter === 'expired' ? '2px solid #dc3545' : '1px solid #ddd' }}
          >
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#721c24' }}>{summary.expired_count}</div>
            <div style={{ color: '#721c24' }}>Expired</div>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="filter-tabs" style={{ marginBottom: '1rem' }}>
        <button
          className={`filter-tab ${statusFilter === 'PENDING' ? 'active' : ''}`}
          onClick={() => setStatusFilter('PENDING')}
          style={{ padding: '0.5rem 1rem', marginRight: '0.5rem', border: 'none', backgroundColor: statusFilter === 'PENDING' ? '#2D1516' : '#e9ecef', color: statusFilter === 'PENDING' ? 'white' : '#333', borderRadius: '4px', cursor: 'pointer' }}
        >
          Pending
        </button>
        <button
          className={`filter-tab ${statusFilter === 'APPROVED' ? 'active' : ''}`}
          onClick={() => setStatusFilter('APPROVED')}
          style={{ padding: '0.5rem 1rem', marginRight: '0.5rem', border: 'none', backgroundColor: statusFilter === 'APPROVED' ? '#2D1516' : '#e9ecef', color: statusFilter === 'APPROVED' ? 'white' : '#333', borderRadius: '4px', cursor: 'pointer' }}
        >
          Approved
        </button>
        <button
          className={`filter-tab ${statusFilter === 'REJECTED' ? 'active' : ''}`}
          onClick={() => setStatusFilter('REJECTED')}
          style={{ padding: '0.5rem 1rem', marginRight: '0.5rem', border: 'none', backgroundColor: statusFilter === 'REJECTED' ? '#2D1516' : '#e9ecef', color: statusFilter === 'REJECTED' ? 'white' : '#333', borderRadius: '4px', cursor: 'pointer' }}
        >
          Rejected
        </button>
        <button
          className={`filter-tab ${statusFilter === '' ? 'active' : ''}`}
          onClick={() => setStatusFilter('')}
          style={{ padding: '0.5rem 1rem', border: 'none', backgroundColor: statusFilter === '' ? '#2D1516' : '#e9ecef', color: statusFilter === '' ? 'white' : '#333', borderRadius: '4px', cursor: 'pointer' }}
        >
          All
        </button>
      </div>

      {/* Documents Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Document Type</th>
              <th>File</th>
              <th>Expiration</th>
              <th>Status</th>
              <th>Uploaded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  No documents found matching the filter.
                </td>
              </tr>
            ) : (
              documents.map((doc) => (
                <tr key={doc.id} className={doc.is_expired ? 'row-expired' : doc.expires_within_30_days ? 'row-warning' : ''}>
                  <td><strong>{doc.customer_name}</strong></td>
                  <td>{doc.document_type_display}</td>
                  <td>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="btn-link"
                      style={{ padding: 0, background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      {doc.original_filename || 'Download'}
                    </button>
                  </td>
                  <td>
                    {doc.expiration_date ? (
                      <span className={doc.is_expired ? 'text-danger' : doc.expires_within_30_days ? 'text-warning' : ''}>
                        {formatDateOnly(doc.expiration_date)}
                        {doc.days_until_expiration !== null && (
                          <small style={{ display: 'block', color: doc.is_expired ? '#dc3545' : doc.expires_within_30_days ? '#ffc107' : '#666' }}>
                            {doc.is_expired ? 'Expired' : `${doc.days_until_expiration} days left`}
                          </small>
                        )}
                      </span>
                    ) : (
                      <span style={{ color: '#666' }}>No expiration</span>
                    )}
                  </td>
                  <td>
                    <span className={getStatusBadgeClass(doc.status)} style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 500 }}>
                      {doc.status_display}
                    </span>
                  </td>
                  <td>
                    <small>{formatDate(doc.created_at)}</small>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setSelectedDocument(doc)}
                        className="btn btn-sm btn-secondary"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem' }}
                      >
                        View
                      </button>
                      {doc.status === 'PENDING' && (
                        <>
                          <button
                            onClick={() => handleApprove(doc)}
                            className="btn btn-sm btn-success"
                            disabled={processing}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => { setSelectedDocument(doc); setShowRejectModal(true); }}
                            className="btn btn-sm btn-danger"
                            disabled={processing}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* View Document Modal */}
      {selectedDocument && !showRejectModal && (
        <div className="modal-overlay" onClick={() => setSelectedDocument(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Tax Document Details</h2>
              <button className="modal-close" onClick={() => setSelectedDocument(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>Customer</label>
                  <div>{selectedDocument.customer_name}</div>
                </div>
                <div>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>Document Type</label>
                  <div>{selectedDocument.document_type_display}</div>
                </div>
                <div>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>Status</label>
                  <span className={getStatusBadgeClass(selectedDocument.status)} style={{ padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                    {selectedDocument.status_display}
                  </span>
                </div>
                <div>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>Expiration Date</label>
                  <div className={selectedDocument.is_expired ? 'text-danger' : selectedDocument.expires_within_30_days ? 'text-warning' : ''}>
                    {selectedDocument.expiration_date ? formatDateOnly(selectedDocument.expiration_date) : 'No expiration'}
                    {selectedDocument.days_until_expiration !== null && !selectedDocument.is_expired && (
                      <span style={{ marginLeft: '0.5rem', color: selectedDocument.expires_within_30_days ? '#ffc107' : '#666' }}>
                        ({selectedDocument.days_until_expiration} days left)
                      </span>
                    )}
                    {selectedDocument.is_expired && (
                      <span style={{ marginLeft: '0.5rem', color: '#dc3545' }}>(Expired)</span>
                    )}
                  </div>
                </div>
                <div>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>File</label>
                  <button onClick={() => handleDownload(selectedDocument)} className="btn-link" style={{ padding: 0, background: 'none', border: 'none', color: '#007bff', cursor: 'pointer' }}>
                    {selectedDocument.original_filename || 'Download Document'}
                  </button>
                </div>
                <div>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>Uploaded</label>
                  <div>{formatDate(selectedDocument.created_at)}</div>
                </div>
              </div>

              {selectedDocument.reviewed_by_name && (
                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>Reviewed By</label>
                  <div>{selectedDocument.reviewed_by_name} on {formatDate(selectedDocument.reviewed_at)}</div>
                  {selectedDocument.review_notes && (
                    <>
                      <label style={{ fontWeight: 600, color: '#666', display: 'block', marginTop: '0.5rem', marginBottom: '0.25rem' }}>Notes</label>
                      <div>{selectedDocument.review_notes}</div>
                    </>
                  )}
                </div>
              )}

              {selectedDocument.status === 'PENDING' && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ fontWeight: 600, color: '#666', display: 'block', marginBottom: '0.25rem' }}>Approval Notes (Optional)</label>
                  <textarea
                    value={approveNotes}
                    onChange={(e) => setApproveNotes(e.target.value)}
                    rows={2}
                    placeholder="Add any notes about this approval..."
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                  />
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem', borderTop: '1px solid #ddd' }}>
              <button onClick={() => setSelectedDocument(null)} className="btn btn-secondary" style={{ padding: '0.5rem 1rem' }}>
                Close
              </button>
              {selectedDocument.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="btn btn-danger"
                    disabled={processing}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedDocument)}
                    className="btn btn-success"
                    disabled={processing}
                    style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px' }}
                  >
                    {processing ? 'Processing...' : 'Approve'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedDocument && (
        <div className="modal-overlay" onClick={() => { setShowRejectModal(false); setRejectNotes(''); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Reject Document</h2>
              <button className="modal-close" onClick={() => { setShowRejectModal(false); setRejectNotes(''); }}>&times;</button>
            </div>
            <div className="modal-body">
              <p>
                You are about to reject the <strong>{selectedDocument.document_type_display}</strong> from <strong>{selectedDocument.customer_name}</strong>.
              </p>
              <div style={{ marginTop: '1rem' }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>Reason for Rejection *</label>
                <textarea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  rows={4}
                  placeholder="Please provide a reason for rejection. This will be visible to the customer."
                  required
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem', borderTop: '1px solid #ddd' }}>
              <button onClick={() => { setShowRejectModal(false); setRejectNotes(''); }} className="btn btn-secondary" style={{ padding: '0.5rem 1rem' }}>
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="btn btn-danger"
                disabled={processing || !rejectNotes.trim()}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                {processing ? 'Processing...' : 'Reject Document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxDocumentsReview;
